/**
 * Pure utility for parsing the HTML body of a calendar event into structured
 * Microsoft Teams metadata + a cleaned-up body string suitable for rendering
 * in our dark-mode panel.
 *
 * Used by `book-meeting-panel` view-mode rendering. Originally lived inside
 * the deleted `meeting-detail-panel` (commit 310aa50); this version factors
 * the logic out so it can be unit-tested without an Angular harness.
 *
 * Why a structured return shape: the panel renders Teams metadata
 * (Join URL, Meeting ID, Passcode, Help, Meeting options) as bespoke rows
 * inside a "JOIN MEETING" card, separately from the free-form agenda. Doing
 * the extraction once at parse time keeps the template simple and avoids
 * sprinkling brittle regex matches across the view layer.
 *
 * No Angular dependencies — sanitisation via `DomSanitizer` happens at the
 * panel boundary so this util can be exercised in vitest without DOM
 * polyfills beyond the standard browser-like environment Angular's vitest
 * runner already provides.
 */

export interface MentionAttendee {
  name: string;
  email?: string;
  jobTitle?: string;
  department?: string;
  picture?: string;
}

export interface ParsedMeetingBody {
  /** First Microsoft Teams join URL discovered in the body. */
  joinUrl?: string;
  /** Numeric meeting ID extracted from "Meeting ID: ..." line. */
  meetingId?: string;
  /** Passcode extracted from "Passcode: ..." line. */
  passcode?: string;
  /** First aka.ms/JoinTeamsMeeting help link. */
  helpUrl?: string;
  /** First teams.microsoft.com/meetingOptions link. */
  organiserOptionsUrl?: string;
  /** Body HTML with Teams boilerplate stripped, SafeLinks unwrapped, light-mode
   *  styles scrubbed, and `mailto:` mentions converted into `.mention-chip`
   *  spans. Empty when the original body was only Teams template content. */
  cleanedHtml: string;
}

const LIGHT_TEXT_COLOURS = new Set([
  'black',
  '#000',
  '#000000',
  'rgb(0, 0, 0)',
  '#333',
  '#333333',
  '#444',
  '#555',
]);

const LIGHT_BG_COLOURS = new Set([
  'white',
  '#fff',
  '#ffffff',
  'rgb(255, 255, 255)',
  '#f5f5f5',
  '#fafafa',
]);

/** Best-effort initials from a display name — same logic the panel uses for
 *  attendee avatars. Kept private so the util is self-contained. */
function getInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0][0]?.toUpperCase() ?? '?';
}

/** Outlook wraps every external link in a SafeLinks redirect. The real URL
 *  is in the `url` query param. We unwrap so the chip / link renders the
 *  actual destination instead of `safelinks.protection.outlook.com/?url=...`. */
function unwrapSafeLink(href: string): string {
  if (!href.includes('safelinks.protection.outlook.com')) return href;
  try {
    const url = new URL(href);
    const real = url.searchParams.get('url');
    if (real) return decodeURIComponent(real);
  } catch {
    /* keep original */
  }
  return href;
}

/** Build a `.mention-chip` element from an attendee record. The chip carries
 *  data attributes so the panel can render a hover card with full identity
 *  details on mouseover (mirroring the old meeting-detail-panel UX). */
function buildMentionChip(doc: Document, att: MentionAttendee): HTMLElement {
  const chip = doc.createElement('span');
  chip.className = 'mention-chip';
  chip.setAttribute('data-mention-name', att.name);
  if (att.email) chip.setAttribute('data-mention-email', att.email);
  if (att.jobTitle) chip.setAttribute('data-mention-title', att.jobTitle);
  if (att.department) chip.setAttribute('data-mention-dept', att.department);
  if (att.picture) chip.setAttribute('data-mention-picture', att.picture);

  if (att.picture) {
    const img = doc.createElement('img');
    img.setAttribute('src', att.picture);
    img.setAttribute('alt', '');
    img.className = 'mention-avatar';
    chip.appendChild(img);
  } else {
    const initials = doc.createElement('span');
    initials.className = 'mention-initials';
    initials.textContent = getInitials(att.name);
    chip.appendChild(initials);
  }

  const label = doc.createElement('span');
  label.textContent = `@${att.name}`;
  chip.appendChild(label);

  return chip;
}

/**
 * Parse a calendar event body into structured Teams metadata + a cleaned
 * body string. Returns `cleanedHtml === rawHtml` when the input has no
 * Teams markers — the panel still renders something meaningful on
 * non-Teams bodies (Google Meet, plaintext, manual notes).
 */
export function parseTeamsBody(
  rawHtml: string,
  attendees: ReadonlyArray<MentionAttendee> = [],
): ParsedMeetingBody {
  if (!rawHtml || typeof rawHtml !== 'string') {
    return { cleanedHtml: '' };
  }

  let parsed: Document;
  try {
    parsed = new DOMParser().parseFromString(rawHtml, 'text/html');
  } catch {
    return { cleanedHtml: rawHtml };
  }

  const root = parsed.body;
  if (!root) return { cleanedHtml: rawHtml };

  const result: ParsedMeetingBody = { cleanedHtml: '' };

  // ── 1. Extract Teams metadata BEFORE stripping the boilerplate so the
  //       JOIN MEETING card can render it as structured rows. ──────────
  const fullText = root.textContent ?? '';

  const meetingIdMatch = fullText.match(/Meeting ID:\s*([\d\s]+?)(?:\s*[\r\n]|\s{2,}|$)/i);
  if (meetingIdMatch) result.meetingId = meetingIdMatch[1].trim();

  const passcodeMatch = fullText.match(/Passcode:\s*(\S+)/i);
  if (passcodeMatch) result.passcode = passcodeMatch[1].trim();

  // Walk all anchors before mutating — order matters for "first match wins".
  for (const linkEl of Array.from(root.querySelectorAll('a[href]'))) {
    const href = unwrapSafeLink(linkEl.getAttribute('href') || '');
    if (!result.joinUrl &&
        (href.includes('teams.microsoft.com/l/meetup-join') ||
         href.includes('teams.microsoft.com/meet'))) {
      result.joinUrl = href;
    } else if (!result.helpUrl && href.includes('aka.ms/JoinTeamsMeeting')) {
      result.helpUrl = href;
    } else if (!result.organiserOptionsUrl &&
               (href.includes('teams.microsoft.com/meetingOptions') ||
                href.includes('teams.microsoft.com/l/meetingOptions'))) {
      result.organiserOptionsUrl = href;
    }
  }

  // ── 2. Drop standalone separator lines (`____________________`). The
  //       Teams template uses these as visual dividers but they look like
  //       horizontal bars in our agenda card — distracting noise. We do NOT
  //       strip the Teams template content itself, because users want to see
  //       the full meeting body in the agenda (the JOIN MEETING card on top
  //       just surfaces the most-used bits for quick access). ──
  for (const node of Array.from(root.querySelectorAll('p, div, span'))) {
    const text = node.textContent?.trim() || '';
    if (/^_{5,}$/.test(text)) node.remove();
  }

  // ── 3. Force dark-mode-friendly inline styles. Outlook inserts hard-coded
  //       black-on-white styles that would render as black-text-on-dark
  //       in our panel. Drop them so the cascade picks up our tokens. ──
  for (const el of Array.from(root.querySelectorAll('[style]'))) {
    const style = (el as HTMLElement).style;
    if (style.color && LIGHT_TEXT_COLOURS.has(style.color.toLowerCase())) {
      style.color = 'inherit';
    }
    if (
      style.backgroundColor &&
      LIGHT_BG_COLOURS.has(style.backgroundColor.toLowerCase())
    ) {
      style.backgroundColor = 'transparent';
    }
    if (style.fontFamily) style.fontFamily = '';
  }

  // ── 4. Convert `mailto:` links to mention chips when we know the
  //       attendee. Forwarded invites may include emails not on the
  //       attendee list — those fall through unchanged. ───────────
  if (attendees.length > 0) {
    const nameMap = new Map<string, MentionAttendee>();
    for (const att of attendees) {
      if (att.name) nameMap.set(att.name.toLowerCase(), att);
    }

    for (const link of Array.from(root.querySelectorAll('a[href^="mailto:"]'))) {
      const name = link.textContent?.replace(/^@/, '').trim() ?? '';
      const att = nameMap.get(name.toLowerCase());
      if (att) link.replaceWith(buildMentionChip(parsed, att));
    }
  }

  // ── 5. Process remaining links — unwrap SafeLinks (in case any survived
  //       the metadata pass), shorten long URLs, ensure target=_blank. ──
  for (const link of Array.from(root.querySelectorAll('a:not([href^="mailto:"])'))) {
    const original = link.getAttribute('href') || '';
    const href = unwrapSafeLink(original);
    if (href !== original) link.setAttribute('href', href);

    const displayText = link.textContent?.trim() || '';
    if (displayText.startsWith('http') && displayText.length > 40) {
      try {
        const url = new URL(displayText);
        const path = url.pathname.length > 15
          ? url.pathname.substring(0, 15) + '...'
          : url.pathname;
        link.textContent = `${url.hostname}${path}`;
      } catch {
        /* keep original */
      }
    }

    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener');
  }

  // ── 6. Drop empty paragraphs / divs / spans left behind by stripping. ──
  const empties = root.querySelectorAll(
    'p, div, span:not(.mention-chip):not(.mention-initials):not(.mention-avatar)',
  );
  for (const node of Array.from(empties)) {
    if (
      !node.textContent?.trim() &&
      !node.querySelector('img, a, br, .mention-chip')
    ) {
      node.remove();
    }
  }

  result.cleanedHtml = root.innerHTML.trim();
  return result;
}
